import { createTextBlock, createButtonRow, createButtonBlock } from '@pendo/services/Adopt';
import {
    AiBlocks,
    BuildingBlock,
    BuildingBlockActions,
    BuildingBlockLayouts,
    BuildingBlockProperties,
} from '@pendo/services/BuildingBlocks';
import { BUILDING_BLOCK_LAYOUT_TYPES } from '@pendo/services/Constants';
import { setBlockContent } from './ai-editor-utils';
import { isMobileApp } from '@/utils/apps';

export const EMBEDDED_PARAGRAPH_ROW_JUSTIFY = 'space-between';

const { widgetIds } = BuildingBlockLayouts;

export function createGeneratedTextBlock ({
    parentBlock,
    theme,
    layoutType,
    textValue,
    themeStyle,
    properties,
    isEmbeddedParagraph = false,
    platform,
}) {
    const textRow = createTextBlock({ parentBlock, theme, layoutType, textValue, properties, platform });
    const textBlock = textRow[0].views[0];
    textBlock.web.themeStyle = themeStyle;

    if (isEmbeddedParagraph) {
        textRow[0].web.displayFlex = true;
        textRow[0].web.justifyContent = EMBEDDED_PARAGRAPH_ROW_JUSTIFY;
        BuildingBlockProperties.updateOrCreateProperty(textBlock, 'layout_width', 'string', '600px');
    }

    return textRow;
}

export function createTextBlockGenerator (
    parentBlock,
    theme,
    text,
    themeStyle,
    isEmbeddedParagraph = false,
    platform,
) {
    return createGeneratedTextBlock({
        parentBlock,
        theme,
        layoutType: BUILDING_BLOCK_LAYOUT_TYPES.LIGHTBOX,
        textValue: text || 'textValue',
        themeStyle,
        properties: [{ name: 'layout_marginBottom', value: '8px', type: 'string' }],
        isEmbeddedParagraph,
        platform,
    });
}

export function subtitleGenerator (parentBlock, theme, text) {
    return createTextBlockGenerator(parentBlock, theme, text, 'subTitle', false /* isEmbeddedParagraph */);
}

export function titleGenerator (parentBlock, theme, text, platform) {
    return createTextBlockGenerator(
        parentBlock,
        theme,
        text,
        'title',
        false /* isEmbeddedParagraph */,
        platform,
    );
}

export function stepTextContentGenerator (parentBlock, theme, text, platform) {
    return createTextBlockGenerator(
        parentBlock,
        theme,
        text,
        'paragraph',
        false /* isEmbeddedParagraph */,
        platform,
    );
}

export function embeddedStepTextContentGenerator (parentBlock, theme, text) {
    return createTextBlockGenerator(
        parentBlock,
        theme,
        text,
        'paragraph',
        true /* isEmbeddedParagraph */,
    );
}

export function buttonGenerator (parentBlock, theme, text, themeStyle, action) {
    return createButtonBlock(parentBlock, theme, {
        actionType: action,
        label: text,
        web: {
            themeStyle,
        },
    });
}

export function primaryButtonGenerator (parentBlock, theme, text, platform) {
    return buttonGenerator(
        parentBlock,
        theme,
        text,
        'primaryButton',
        BuildingBlockActions.actionTypes.advanceGuide,
        platform,
    );
}

export function secondaryButtonGenerator (parentBlock, theme, text) {
    return buttonGenerator(parentBlock, theme, text, 'secondaryButton', BuildingBlockActions.actionTypes.dismissGuide);
}

export function secondaryButtonWithPreviousActionGenerator (parentBlock, theme, text) {
    return buttonGenerator(parentBlock, theme, text, 'secondaryButton', BuildingBlockActions.actionTypes.previousStep);
}

function getKeyFromBlock (block) {
    return block.web.artifactKey || block.web.themeStyle;
}

export function createArtifactKeyToBlockMap (buildingBlocks) {
    const containerBlock = AiBlocks.getContainerBlock(buildingBlocks);

    return containerBlock.views.reduce((acc, block) => {
        if (block.widget === widgetIds.rowBlock) {
            block.views.forEach((childBlock) => {
                const key = getKeyFromBlock(childBlock);
                acc[key] = childBlock;
            });

            return acc;
        }

        const key = getKeyFromBlock(block);
        acc[key] = block;

        return acc;
    }, {});
}

export function generateOrUpdateBlock ({
    existingBlocks,
    key,
    stepArtifact,
    containerBlock,
    theme,
    generator,
    platform,
}) {
    const text = stepArtifact[key];
    const existingBlock = existingBlocks[key];

    if (existingBlock) {
        setBlockContent({ buildingBlock: existingBlock, content: text, platform });

        return existingBlock;
    }

    const generatedBlock = generator(containerBlock, theme, text, platform)[0];

    return generatedBlock;
}

function generateButtons ({ buttonGenerators, existingBlocks, stepArtifact, containerBlock, theme, platform, isMobile }) {
    return Object.entries(buttonGenerators).map(([buttonKey, buttonGenerator], index) => {
        const button = generateOrUpdateBlock({
            existingBlocks,
            key: buttonKey,
            stepArtifact,
            containerBlock,
            theme,
            generator: buttonGenerator,
            platform,
        });

        if (!isMobile && index !== 0) {
            BuildingBlockProperties.updateOrCreateProperty(button, 'layout_marginLeft', 'dimen', '5px');
        }

        return button;
    });
}

function addButtonsToEmbeddedGuide ({ buttons, newChildren }) {
    const embeddedButton = buttons[0];
    const embeddedTextAndButtonRow = newChildren.find(
        (child) => child.web.justifyContent === EMBEDDED_PARAGRAPH_ROW_JUSTIFY,
    );
    const rowDomId = embeddedTextAndButtonRow.web.domId;
    embeddedButton.web.rowDomId = rowDomId;
    embeddedTextAndButtonRow?.views?.push(embeddedButton);
}

function addButtonsToMobileGuide ({ buttons, newChildren, platform, theme, buildingBlocks }) {
    // for mobile guides, each button should be in a separate row.
    buttons.forEach((button) => {
        const buttonRow = createButtonRow({
            alignment: 'center',
            layoutType: BUILDING_BLOCK_LAYOUT_TYPES.BUTTON_ROW,
            platform,
            theme,
            allBlocks: buildingBlocks,
            children: [button],
        });
        newChildren.push(buttonRow);
    });
}

function addButtonsToWebGuide ({ buttons, newChildren, platform, theme, buildingBlocks }) {
    const buttonRow = createButtonRow({
        alignment: 'flex-end',
        platform,
        theme,
        allBlocks: buildingBlocks,
        children: buttons,
    });
    newChildren.push(buttonRow);
}

function processButtons ({
    buttonGenerators,
    existingBlocks,
    stepArtifact,
    containerBlock,
    theme,
    platform,
    isMobile,
    isEmbeddedGuide,
    newChildren,
    buildingBlocks,
}) {
    const buttons = generateButtons({
        buttonGenerators,
        existingBlocks,
        stepArtifact,
        containerBlock,
        theme,
        platform,
        isMobile,
    });

    if (isEmbeddedGuide) {
        addButtonsToEmbeddedGuide({ buttons, newChildren });
    } else if (isMobile) {
        addButtonsToMobileGuide({ buttons, newChildren, platform, theme, buildingBlocks });
    } else {
        addButtonsToWebGuide({ buttons, newChildren, platform, theme, buildingBlocks });
    }
}

export function createBuildingBlocksFromTemplate ({
    theme,
    width,
    isEmbeddedGuide = false,
    buildingBlocks,
    stepArtifact,
    artifactKeysToGenerators,
    platform = 'web',
}) {
    const isMobile = isMobileApp(platform);
    AiBlocks.setStepWidth(buildingBlocks, width, isEmbeddedGuide, isMobile);

    const existingBlocks = createArtifactKeyToBlockMap(buildingBlocks);
    const containerBlock = AiBlocks.getContainerBlock(buildingBlocks);
    const newChildren = [];
    const existingChildren = containerBlock.views;
    const closeRow = existingChildren.find((b) => b.views?.[0]?.widget === widgetIds.closeButtonBlock);
    const closeBlock = containerBlock.views.find((block) => block.widget === widgetIds.closeButtonBlock);

    if (closeBlock) {
        newChildren.push(closeBlock);
    }

    if (closeRow && isMobile) {
        newChildren.push(closeRow);
    }

    Object.entries(artifactKeysToGenerators).forEach(([key, value]) => {
        if (key === 'buttons') {
            processButtons({
                buttonGenerators: value,
                existingBlocks,
                stepArtifact,
                containerBlock,
                theme,
                platform,
                isMobile,
                isEmbeddedGuide,
                newChildren,
                buildingBlocks,
            });

            return;
        }

        const block = generateOrUpdateBlock({
            existingBlocks,
            key,
            stepArtifact,
            containerBlock,
            theme,
            generator: value,
            newChildren,
            platform,
        });

        if (block.web.rowDomId) {
            const row = BuildingBlock.findBlockByDomId(buildingBlocks, block.web.rowDomId);
            if (row) {
                row.views = [block];

                return newChildren.push(row);
            }

            block.web.rowDomId = undefined;
        }

        newChildren.push(block);
    });

    containerBlock.views = newChildren;

    applyThemeToStepBlocks({ buildingBlocks, theme, isEmbeddedGuide });

    return buildingBlocks;
}

export function applyThemeToStepBlocks ({ buildingBlocks, theme, isEmbeddedGuide }) {
    return BuildingBlock.applyThemeToBuildingBlocks({
        theme,
        buildingBlocks,
        options: { isEmbed: isEmbeddedGuide },
    });
}

export function canUseAIGuideEditor (
    guideConversationalAISegmentFlag,
    aIGuideContentEnabledMetadata,
    enableGenerateAIGuideContentConsentFlag,
) {
    return guideConversationalAISegmentFlag && aIGuideContentEnabledMetadata && enableGenerateAIGuideContentConsentFlag;
}

export function createArtifactStepsFromStep (step) {
    return AiBlocks.convertStepBlocksToAIBlocks(step);
}

export function makeConvoUiStepKey ({ stepId, index, themeId, suffix = '' }) {
    return `Step-${stepId}-${index}-${themeId}${suffix ? `-${suffix}` : ''}`;
}

export function getTemplateParam ({ isMobile, isEmbed }) {
    if (isMobile) {
        return 'mobileAnnouncement';
    }

    if (isEmbed) {
        return 'embeddedBanner';
    }
}
